import { seccionOculta } from '../../lib/restricciones';
import { useState, useEffect, useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Collapse, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Typography, Divider, alpha, useTheme, Chip, Badge, Tooltip,
  type SxProps, type Theme,
} from '@mui/material';
import {
  Dashboard, People, HomeWork, Event, BarChart, AttachMoney,
  Group, Business, EmojiEvents, Logout, AddHome, AddHomeWork, Calculate,
  CompareArrows, ExpandMore, ChevronRight, NoteAdd, History, Map, WorkspacePremium, School, Videocam, Tune,
  SwapHoriz, DashboardCustomize, Chat, Campaign, Slideshow, Insights, AccountBalance, Gavel, HelpOutline, MenuBook, EventNoteRounded,
  WarningAmber, PersonOutline, AutoAwesome, QueryStats, Diamond, Storefront, AccountBalanceWallet, RssFeed,
  VerifiedUser, Payments, ForumOutlined, Settings, Language, HomeRounded, ErrorOutline, FactCheckOutlined,
  Science, LocalOffer, Inventory2, ViewKanban,
  Public, BookmarkBorder, PollOutlined, Code, Hub, HowToReg, ReceiptLong, BugReport, EventAvailable,
  TrendingUp,
} from '@mui/icons-material';
import { usePermissions } from '../../hooks/usePermissions';
import { useFeatureFlags } from '../../hooks/useFeatureFlag';
import { useGetRecentBlogPostsQuery } from '../../store/api/blogApi';
import { useNovedadesLeidas, marcarNovedadLeida } from '../../features/blog/novedadesLeidas';
import { useListConversationsQuery } from '../../store/api/chatApi';
import { useListCrmEscalationsQuery } from '../../store/api/crmPhylaxApi';
import { ROLES_PHYLAX_CRM, usePuedeVerPhylaxCrm } from '../../features/phylax/usePuedeVerPhylaxCrm';
import { usePhylaxBooster } from '../../features/phylax/booster/usePhylaxBooster';
import { useGetMyProfileQuery } from '../../store/api/usersApi';
import { getCrmPalette } from '../../theme/brand';
import { useThemeMode } from '../../theme/ThemeModeContext';
import { BoosterMark } from '../BoosterMark';
import { AgentAvatar } from '../AgentAvatar';
import type { CircleTier } from '../../store/api/usersApi';
import { useLogoutMutation } from '../../store/api/authApi';
import { baseApi } from '../../store/api/baseApi';
import { getPersistorRef } from '../../store/persistorRef';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppStore';
import { useEffectiveTenant } from '../../hooks/useEffectiveTenant';
import { logout as logoutAction } from '../../features/auth/authSlice';
import { SelloDePlataforma } from './SelloDePlataforma';

export const DRAWER_WIDTH = 256;
export const DRAWER_WIDTH_COLLAPSED = 72;

interface NavSubItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  permission?: string | string[];
  role?: string;
  allowedRoles?: string[]; // visible solo para estos roles (alternativa a role único)
  hideForRoles?: string[]; // ocultar para estos roles
  /**
   * Se oculta para quien TENGA este permiso. Es un permiso al revés, a propósito.
   *
   * Sirve para sacarle una entrada del menú a UNA persona sin tocar su rol ni el de los
   * demás. Con la forma normal —exigir un permiso para ver— habría que dárselo a todos los
   * roles que hoy lo ven, y cualquier olvido le saca el menú a alguien más. Así, por
   * defecto no lo tiene nadie y nada cambia hasta que se lo asignan a un usuario puntual
   * (`User.permissions`).
   *
   * Solo esconde el ítem: la página sigue siendo alcanzable escribiendo la URL. Si hace
   * falta bloquearla de verdad, va por `deniedPermissions` sobre el permiso de la página.
   */
  hideIfPermission?: string;
  /**
   * Se muestra a quien TENGA este permiso, aunque su rol no lo habilite.
   *
   * El complemento de `hideIfPermission`: uno saca, este agrega. Los dos apuntan a lo
   * mismo — poder ajustar el menú de UNA persona sin mover el rol de nadie.
   */
  showIfPermission?: string;
  nativeOnly?: boolean;    // feature Imperium-only — se oculta para tenants booster
  soloConPhylax?: boolean; // Phylax: Imperium o Booster con plan vigente (usePuedeVerPhylaxCrm)
  boosterOnly?: boolean;   // feature SOLO para tenants booster — se oculta en Imperium
  featureKey?: string;     // feature flag key — si existe, el sub-item se esconde si el flag está off
  planFeature?: string;    // feature del PLAN — si existe, se oculta si el plan del tenant no la incluye
}

/**
 * ¿Este ítem está "activo" en `pathname`?
 *
 *  - Con `activePrefix`, cualquier ruta que empiece así lo prende (la ficha de un tenant
 *    prende "Tenants" aunque su path sea otro).
 *  - Con children o `exact`, SOLO su path exacto: sin eso, un ítem cuyo path es prefijo
 *    de otro (`/platform` y `/platform/coupons`) se prendía junto con su vecino.
 *  - Si no, su path o cualquier sub-ruta real (`path + '/'`).
 */
export function estaActivo(
  item: { path: string; exact?: boolean; activePrefix?: string; children?: unknown[] },
  pathname: string,
): boolean {
  if (item.activePrefix && pathname.startsWith(item.activePrefix)) return true;
  const soloExacto = !!item.children?.length || !!item.exact;
  if (soloExacto) return pathname === item.path;
  return pathname === item.path || pathname.startsWith(item.path + '/');
}

interface NavItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  permission?: string | string[];
  role?: string;
  allowedRoles?: string[]; // visible solo para estos roles
  hideForRoles?: string[]; // ocultar para estos roles (inverso de role)
  /** Se oculta para quien TENGA este permiso. Ver el comentario en el tipo de arriba. */
  hideIfPermission?: string;
  /** Se muestra a quien TENGA este permiso, aunque su rol no lo habilite. */
  showIfPermission?: string;
  nativeOnly?: boolean;    // feature Imperium-only — se oculta para tenants booster
  soloConPhylax?: boolean; // Phylax: Imperium o Booster con plan vigente (usePuedeVerPhylaxCrm)
  boosterOnly?: boolean;   // feature SOLO para tenants booster — se oculta en Imperium
  /** Si true, "activo" requiere match EXACTO de pathname (sin startsWith).
   *  Útil cuando otro item peer tiene un path que es sub-ruta de este. */
  exact?: boolean;
  /** Prefijo extra que también cuenta como "activo". Para secciones que se navegan
   *  desde ADENTRO (ej. Agente Virtual): el item apunta a una pantalla, pero la
   *  sección abarca varias rutas hermanas que no cuelgan de ese path. */
  activePrefix?: string;
  /** Logo-imagen que REEMPLAZA icono+texto en modo expandido (ej. Phylax).
   *  En modo colapsado se sigue usando `icon`, y `title` queda para tooltips. */
  titleImage?: string;
  badge?: string;
  featureKey?: string;   // feature flag key — si existe, el ítem se esconde si el flag está off
  planFeature?: string;  // feature del PLAN — si existe, se oculta si el plan del tenant no la incluye
  /** Resalta visualmente el item (gradiente dorado + glow). Pensado para accesos comerciales. */
  highlighted?: boolean;
  children?: NavSubItem[];
}

export interface NavGroup {
  label?: string;
  items: NavItem[];
  /** El menú fijo de un usuario (ver `menuFijoDe`): un mismo path puede venir repetido. */
  fijo?: boolean;
}

/**
 * MENÚ FIJO POR USUARIO. (07/10/2026, usuario del departamento contable)
 *
 * Con permisos `menu:solo:<path>` el usuario ve SOLO esas entradas, en ESE orden y sin
 * secciones: una lista plana. Es el mismo idioma que `menu:hide_*` —se asigna en
 * `User.permissions` a una persona puntual, sin tocar su rol— pero al revés: en vez de
 * sacar de a una, se dice qué queda. Para un menú de 13 entradas sobre un admin que ve 40,
 * sacar de a una era inmanejable y cualquier ítem nuevo del menú se le colaba.
 *
 * Cada entrada sigue pasando por el filtro normal (rol, permisos, flags, plan): esto
 * elige y ordena, no habilita. Un hijo (Pagos, de Facturación) sube como entrada suelta y
 * hereda el `nativeOnly` del padre. Si dos ítems comparten path (Reporte Comercial y
 * Métricas del Parte), van los dos y el render se queda con el primero visible.
 *
 * SECCIONES: un `menu:seccion:<Título>` entre las entradas abre una sección con ese título;
 * lo que viene antes de la primera va sin título (el Dashboard, arriba de todo).
 *
 * Solo arma el menú: las páginas siguen alcanzables por URL según los permisos del rol.
 */
export const MENU_SOLO = 'menu:solo:';
export const MENU_SECCION = 'menu:seccion:';

export function menuFijoDe(permisos: readonly string[] | undefined, grupos: readonly NavGroup[]): NavGroup[] | null {
  const marcas = (permisos ?? []).filter((p) => p.startsWith(MENU_SOLO) || p.startsWith(MENU_SECCION));
  if (!marcas.some((p) => p.startsWith(MENU_SOLO))) return null;
  const todos: NavItem[] = [];
  for (const g of grupos) {
    for (const it of g.items) {
      todos.push(it);
      for (const c of it.children ?? []) todos.push({ ...c, nativeOnly: c.nativeOnly || it.nativeOnly });
    }
  }
  const salida: NavGroup[] = [{ items: [], fijo: true }];
  for (const marca of marcas) {
    if (marca.startsWith(MENU_SECCION)) {
      salida.push({ label: marca.slice(MENU_SECCION.length), items: [], fijo: true });
      continue;
    }
    const path = marca.slice(MENU_SOLO.length);
    for (const it of todos) {
      if (it.path !== path) continue;
      // Suelto: un padre elegido por su path va sin el submenú.
      const { children: _sinHijos, ...suelto } = it;
      salida[salida.length - 1].items.push(suelto);
    }
  }
  return salida.filter((g) => g.items.length > 0);
}

/**
 * Compara un childPath (que puede traer query string ej. "/properties?scope=network"
 * o "/support/analitica?tab=agents") contra la pathname + search actual del browser.
 *
 * - Si los pathnames no matchean → no activo.
 * - Si el childPath NO tiene query → activo solo cuando la URL actual tampoco
 *   tiene parámetros distintivos definidos (es el "default" de esa pathname).
 * - Si el childPath tiene `?key=val` → activo solo cuando la URL actual tiene
 *   exactamente esos pares (todos coincidir).
 *
 * Esto evita que múltiples sub-items que comparten pathname (ej. "Shark" en
 * /properties y "Imperium" en /properties?scope=network) queden los dos
 * activos al mismo tiempo.
 */
function isChildPathActive(childPath: string, currentPathname: string, currentSearch: string): boolean {
  const [path, queryStr = ''] = childPath.split('?');
  if (currentPathname !== path) return false;

  const currentParams = new URLSearchParams(currentSearch);
  if (!queryStr) {
    // childPath sin query → activo solo si current no tiene NINGÚN param que
    // distingue (tab o scope). Otros params como page, sort, etc. no afectan.
    return !currentParams.get('tab') && !currentParams.get('scope');
  }
  // childPath con query → cada param del child debe matchear con el current.
  const childParams = new URLSearchParams(queryStr);
  for (const [key, val] of childParams) {
    if (currentParams.get(key) !== val) return false;
  }
  return true;
}

/**
 * Lo único que ve un agente en formación. Es lista blanca a propósito: el menú tiene
 * items sin `permission` declarado, así que cualquier gateo por permisos lo dejaría
 * pasar. Cuando el admin lo promueve a `agent`, esto deja de aplicar y ve todo.
 */
const TRAINEE_PATHS = new Set(['/capacitaciones', '/chat', '/profile']);

/**
 * Un ícono del menú con su propio fondo, como el isotipo de Phylax.
 *
 * Es un componente y no un `<Box>` suelto en el array de NAV a propósito: ese array se
 * construye una sola vez al cargar el módulo, y el dorado lo muta `applyCrmAccent` según la
 * marca. Un color leído ahí queda congelado en el de Imperium y en Booster se ve mal.
 * Leyéndolo acá adentro, se resuelve en cada render. (21/08/2026)
 */
function IconoConFondo({ children }: { children: React.ReactNode }) {
  const marca = getCrmPalette().brand;
  return (
    <Box sx={{
      width: 18, height: 18, borderRadius: '5px', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: marca.golden, color: marca.onAccent,
      '& svg': { fontSize: 12 },
    }}>
      {children}
    </Box>
  );
}

/** Exportado para fijar el ORDEN del menú en tests (ver Sidebar.test.tsx). */
export const NAV_GROUPS: NavGroup[] = [
  {
    items: [
      { title: 'Dashboard',    path: '/dashboard',   icon: <Dashboard fontSize="small" />, featureKey: 'dashboard', hideForRoles: ['booster_support'] },
    ],
  },
  {
    label: 'Gestión',
    items: [
      { title: 'Clientes',     path: '/customers',   icon: <People fontSize="small" />,       permission: 'customers:read_own', featureKey: 'customers' },
      {
        title: 'Propiedades', path: '/properties', icon: <HomeWork fontSize="small" />,
        // (01/09/2026) El tenant_admin externo ve Propiedades > Corporativo (todo el stock del
        // tenant), como pidió el ticket de Booster. NO ve "Mías" (no tiene asignadas) ni "Nueva".
        permission: 'properties:read', featureKey: 'properties', hideForRoles: ['booster_support'],
        children: [
          /**
           * Alta rápida para cargar una propiedad.
           *
           * ⛔ VA POR TENANT, NO POR ROL. Estaba en `allowedRoles: ['tenant_agent']` y eso
           * dejaba afuera a los `agent` de los tenants Booster: medido contra la base, 11 de
           * los 20 usuarios Booster con `properties:write` no veían el menú —entre ellos
           * Néstor, de Elite Real Estate, que lo reportó a soporte: "voy a donde dice MÍAS y
           * no logro encontrar donde subir una nueva propiedad" (17/09/2026)—.
           *
           * ⛔ Y NO ALCANZA CON SACAR EL `allowedRoles`. En Imperium hay 138 usuarios con
           * `properties:write`; sin el gate por tenant, a todos ellos les aparecería un menú
           * que no les corresponde. El alta rápida es de las inmobiliarias suscriptoras: en
           * Imperium la propiedad entra por Captaciones.
           *
           * ⛔ VAN LOS DOS ROLES DE AGENTE, `agent` Y `tenant_agent`, y no alcanza con uno.
           * Los tenants Booster vienen en dos formas y el rol depende de cuál (medido el
           * 17/09/2026):
           *   · SOLO (dueño-operador, un usuario): el rol es `tenant_agent`. Son nueve.
           *   · CON EQUIPO (un `tenant_admin` + varios `agent`): Elite Real Estate y JR
           *     BROKER. Son ocho agentes.
           * Dejando solo `tenant_agent` se caen los ocho de equipo, Néstor incluido — que es
           * justamente quien reportó que no lo veía.
           *
           * ⛔ Y EL `tenant_admin` NO VA, a propósito: no tiene propiedades asignadas y el
           * alta es tarea del agente. Es la misma decisión del 01/09/2026 anotada arriba, para
           * "Mías" y para ésta.
           */
          { title: 'Nueva propiedad', path: '/properties/nueva', icon: <AddHomeWork fontSize="small" />, permission: 'properties:write', boosterOnly: true, allowedRoles: ['agent', 'tenant_agent'] },
          // Mías: marcado/curado → BookmarkBorder.
          { title: 'Mías',     path: '/properties/mine',          icon: <BookmarkBorder fontSize="small" />, permission: 'properties:read', hideForRoles: ['office_admin', 'tenant_admin'] },
          // Oficina propia → Business (edificio).
          { title: 'Corporativo', path: '/properties',               icon: <Business fontSize="small" />,       permission: 'properties:read', hideForRoles: ['tenant_agent'] },
          // Toda la red Imperium → Public (globo, red).
          { title: 'Corporativo', path: '/properties?scope=network', icon: <Public fontSize="small" />,         permission: 'properties:read', planFeature: 'multi_office', hideForRoles: ['agent', 'notary_sale', 'notary_rent', 'read_only', 'tenant_agent'] },
          // Bolsa cross-tenant (red Booster) → Storefront.
          { title: 'Bolsa de Propiedades', path: '/bolsa-propiedades', icon: <Storefront fontSize="small" />, permission: 'properties:read', hideForRoles: ['tenant_admin', 'booster_support', 'notary_sale', 'notary_rent'] },
        ],
      },
      { title: 'Subastas',     path: '/auctions',    icon: <Gavel fontSize="small" />,         permission: 'properties:write',   featureKey: 'auctions', hideForRoles: ['agent', 'agent_senior', 'agent_junior'] },
      // Soporte entra a /captaciones por link cuando lo necesita: el acceso queda, el ítem
      // no — es una pantalla operativa de la inmobiliaria, no de soporte.
      { title: 'Captaciones',  path: '/captaciones', icon: <AddHome fontSize="small" />,      permission: 'captaciones:read',   featureKey: 'captaciones', hideForRoles: ['tenant_admin', 'office_admin', 'booster_support'] },
      { title: 'Escrib. Alquileres', path: '/notaria/alquileres', icon: <VerifiedUser fontSize="small" />, permission: 'rental_cases:read' },
      { title: 'Boletos de reserva',    path: '/notaria/ventas',     icon: <VerifiedUser fontSize="small" />, permission: 'sale_cases:read' },
      { title: 'Mis Operaciones',   path: '/mis-operaciones',    icon: <Gavel fontSize="small" />,        permission: 'agent_cases:read' },
      {
        title: 'Tasaciones',
        path: '/tasaciones',
        icon: <Calculate fontSize="small" />,
        permission: 'tasaciones:read',
        featureKey: 'tasaciones',
        // Tasaciones es una herramienta del agente — los admins (platform y
        // office) no la usan operativamente.
        hideForRoles: ['tenant_admin', 'office_admin'],
        children: [
          { title: 'Nueva Tasación', path: '/tasaciones/nueva',    icon: <NoteAdd fontSize="small" />,       permission: 'tasaciones:read' },
          { title: 'Comparador',     path: '/tasaciones/comparar', icon: <CompareArrows fontSize="small" />, permission: 'tasaciones:read' },
          { title: 'Historial',      path: '/tasaciones',          icon: <History fontSize="small" />,       permission: 'tasaciones:read' },
        ],
      },
      { title: 'Calendario',    path: '/calendar',       icon: <Event fontSize="small" />,           permission: 'calendar:read_own',    featureKey: 'calendar' },
    ],
  },
  {
    // Integraciones va ARRIBA de Comunidad: las consultas del Agente Virtual son de
    // alguien esperando respuesta AHORA, y al fondo del menú se pasaban por alto —
    // llegaron a acumularse 29 sin atender hace más de tres días. (pedido de dirección
    // 04/08)
    label: 'Integraciones',
    items: [
      {
        // Modelo corporativo (PHYLAX_IMPERIUM_PLAN.md): los MENSAJES son del admin del
        // tenant; el agente solo ve las consultas elevadas sobre SUS propiedades, y el
        // office_admin lo de su oficina (y puede derivar dentro de ella).
        // Sin hijos a propósito: la sección se navega desde ADENTRO (PhylaxShell trae
        // sus propias pestañas Resumen/Consultas/Conversaciones/Visitas, filtradas por
        // el mismo criterio de rol). Si volvés a agregar hijos, sincronizá los roles con
        // NAV en PhylaxShell.tsx.
        //
        // El isotipo y "Phylax" como texto normal: el logo entero traía un interletrado
        // propio que quedaba como un cuerpo extraño entre los demás ítems. (dirección 02/08)
        //
        // En SU verde y no en el dorado adaptado (20/08/2026): el Agente Virtual lo hace
        // Phylax, y con el squircle dorado se leía como un módulo más de la casa. Verde,
        // el menú deja claro de quién es la sección.
        title: 'Phylax', path: '/agente',
        icon: <Box component="img" src="/phylax-mark.svg" alt="" sx={{ width: 18, height: 18, display: 'block', borderRadius: '5px' }} />,
        activePrefix: '/agente',
        allowedRoles: ROLES_PHYLAX_CRM,
        // Imperium (por `phylax_crm`, en usePuedeVerPhylaxCrm) y SIEMPRE las inmobiliarias
        // de Booster: sin suscripción la sección les muestra qué incluye y cuánto sale.
        // (22/10/2026)
        soloConPhylax: true,
      },
      /**
       * La tienda. Estuvo fuera del menú el 23/10/2026 (no había nada que ofrecer) y volvió el
       * mismo día con las entradas del Imperium Summit.
       */
      {
        title: 'Marketplace', path: '/marketplace',
        // El fondo va en el ÍCONO y no en toda la fila: pintada entera se leía como un
        // botón de acción y le ganaba a Phylax, que es lo que hay que atender. (21/08/2026)
        icon: <IconoConFondo><Storefront fontSize="small" /></IconoConFondo>,
        featureKey: 'marketplace',
        /**
         * ⛔ La tienda es para TODOS —admins de tenant y de oficina incluidos—: compran como
         * un agente más. Solo se esconde a soporte, que administra y no compra. (11/09/2026)
         */
        hideForRoles: ['booster_support'],
      },
      /**
       * La ADMINISTRACIÓN del marketplace: SOLO SOPORTE, que es quien lo configura.
       * (11/09/2026) Los admins de tenant la tenían para "mirar"; ahora compran en la
       * tienda como cualquier agente y no entran acá. El backend lo corta igual
       * (`marketplace.routes`). `booster_admin` pasa por superset de roles.
       */
      {
        title: 'Administrar Marketplace', path: '/admin/marketplace',
        icon: <IconoConFondo><Storefront fontSize="small" /></IconoConFondo>,
        featureKey: 'marketplace',
        allowedRoles: ['booster_support'],
      },
      /**
       * Links de pago (dLocal): cobrar una reserva con tarjeta mandándole un link al
       * cliente. Es de IMPERIUM y de su admin: el cobro sale por la cuenta dLocal de
       * Imperium, así que ni las inmobiliarias Booster ni el soporte de plataforma lo
       * ven. El asesor genera el suyo desde la ficha de su propiedad reservada, sin pasar
       * por acá. (14/09/2026, pedido del user)
       */
      {
        title: 'Links de pago', path: '/links-de-pago',
        // El isotipo de dLocal, con su morado: el cobro lo hace dLocal, y en el menú se
        // lee de quién es, como pasa con Phylax.
        icon: <Box component="img" src="/dlocal-mark.png" alt="" sx={{ width: 18, height: 18, display: 'block', borderRadius: '5px' }} />,
        // Es del ADMIN de Imperium: soporte no cobra, y el operador de Booster menos.
        allowedRoles: ['tenant_admin', 'office_admin'],
        // Solo Imperium: el cobro sale por su cuenta dLocal (ver quienVeLinksDePago).
        nativeOnly: true,
      },
    ],
  },
  {
    // Comunidad va ARRIBA de "Mi espacio": es lo que la inmobiliaria le comunica al
    // equipo —mensajes y novedades— y al fondo del menú se perdía. (pedido de dirección)
    label: 'Comunidad',
    items: [
      { title: 'Mensajes',     path: '/chat',        icon: <Chat fontSize="small" /> },
      /**
       * Novedades NO es `nativeOnly`: cualquier inmobiliaria le comunica cosas a su
       * equipo, no es una función interna de Imperium. Estaba oculta para los otros
       * nueve tenants sin más razón que haber nacido acá.
       *
       * Se puede abrir recién ahora porque las novedades se recortan por inmobiliaria:
       * hasta el 17/08/2026 el feed era global y prender el menú les habría mostrado las
       * 46 novedades de Imperium. El `featureKey` sigue, así que se apaga por tenant
       * desde los flags si alguno no la quiere.
       */
      { title: 'Novedades',    path: '/blog',        icon: <Campaign fontSize="small" />, featureKey: 'blog' },
      // Eventos (EVENTOS_PLAN.md): solo Imperium, detrás de la flag `eventos`.
      { title: 'Eventos',      path: '/eventos',     icon: <EventAvailable fontSize="small" />, permission: 'eventos:read', featureKey: 'eventos', nativeOnly: true },
      /**
       * Anuncios: la lámina a pantalla completa que frena al equipo al entrar. Hermana de
       * Novedades —el mismo trabajo con otro formato— y por eso vive acá al lado.
       *
       * `allowedRoles` y no `permission`: es una pantalla de quien COMUNICA, y soporte
       * entra sí o sí (normalmente es quien manda estas cosas). No lleva `nativeOnly`,
       * porque cualquier inmobiliaria le avisa cosas a su equipo — el backend ya recorta
       * los anuncios por tenant.
       */
      /**
       * ⛔ SOLO PLATAFORMA. (08/09/2026, pedido del user)
       *
       * La lámina de un anuncio FRENA la pantalla de todo el mundo al entrar, y la `clave`
       * que la marca como vista es para siempre: si un admin de una inmobiliaria manda una
       * mal escrita, no hay forma de que su gente la vuelva a ver corregida. Es una
       * herramienta de comunicación de la plataforma, no de cada tenant.
       */
      { title: 'Anuncios',     path: '/anuncios',    icon: <Slideshow fontSize="small" />, allowedRoles: ['booster_support', 'booster_admin'] },
    ],
  },
  {
    // Espacio personal del agente: lo financiero + carrera + formación. Se separó de
    // "Gestión" (que quedaba sobrecargada) para aliviar el menú.
    label: 'Mi espacio',
    items: [
      { title: 'Finanzas',      path: '/finances',       icon: <AttachMoney fontSize="small" />,     permission: 'finances:read',        featureKey: 'finances' },
      // Comisiones salió de Finanzas y pasó a ser página propia bajo Facturación
      // (pedido de dirección). El grupo "Facturación" está gateado a tenant_admin, así
      // que el admin de OFICINA —que hoy veía el tab dentro de Finanzas— lo tendría
      // perdido: se lo dejamos acá para no recortarle un acceso que ya tenía.
      { title: 'Comisiones',    path: '/admin/comisiones', icon: <ReceiptLong fontSize="small" />,   permission: 'finances:read',        featureKey: 'finances', allowedRoles: ['office_admin'] },
      { title: 'Mi Wallet',     path: '/wallet',          icon: <AccountBalanceWallet fontSize="small" />, permission: 'wallet:read', featureKey: 'wallet', hideForRoles: ['tenant_admin', 'office_admin'], nativeOnly: true },
      // El agente ve sus créditos en el perfil (/profile?tab=credits), no acá: un ítem
      // propio en el menú para mirar un saldo era demasiado peso para lo que es.
      { title: 'Mis créditos',  path: '/mis-creditos',    icon: <Diamond fontSize="small" />, permission: 'investments:catalog_read', featureKey: 'credits', hideForRoles: ['tenant_admin', 'office_admin', 'booster_support', 'agent'], nativeOnly: true },
      { title: 'Mi Carrera',    path: '/mi-carrera',     icon: <WorkspacePremium fontSize="small" />, permission: 'finances:read',        featureKey: 'mi-carrera', hideForRoles: ['tenant_admin', 'office_admin'], nativeOnly: true },
      // Booster también: ve solo sus cursos (los de Imperium no le llegan) y el de tutoriales
      // del CRM, que es para todas las inmobiliarias Booster. (27/10/2026)
      { title: 'Academy', path: '/capacitaciones', icon: <School fontSize="small" />,         permission: 'capacitaciones:read',  featureKey: 'capacitaciones', hideForRoles: ['office_admin'] },
      // El admin de oficina no ve Academy, pero sí agenda sesiones en vivo para su oficina (01/10/2026).
      { title: 'En vivo',  path: '/capacitaciones?tab=vivo', icon: <Videocam fontSize="small" />, permission: 'live_sessions:write', featureKey: 'en-vivo', allowedRoles: ['office_admin'] },
    ],
  },
  {
    label: 'Reporte',
    items: [
      // (27/08/2026) "Parte diario" sale del menú: el parte dejó de enviarse — se arma solo
      // y se actualiza en vivo — así que ya no hay nada que ir a hacer ahí. Los agentes
      // miran sus números en "Métricas del Parte". La ruta /reporte sigue viva para los
      // links que ya circulan (Gustavo, novedades, mails); lo que se saca es la invitación
      // diaria a un trámite que no existe.
      // Vista self-only del Reporte Comercial: SOLO agentes (read_own; los admin
      // tienen read_all/read_office y lo ven como "Reporte Comercial" en Análisis).
      { title: 'Métricas del Parte', path: '/reporte-comercial', icon: <QueryStats fontSize="small" />, permission: 'analytics:read_own', featureKey: 'reporte-comercial' },
    ],
  },
  {
    label: 'Análisis',
    items: [
      { title: 'Analytics',      path: '/analytics',    icon: <BarChart fontSize="small" />,    permission: 'analytics:read_office', featureKey: 'analytics', hideForRoles: ['tenant_admin', 'office_admin'] },
      { title: 'Estad. Proceso', path: '/estadisticas-proceso', icon: <Insights fontSize="small" />, permission: ['analytics:read_office', 'analytics:read_all'], featureKey: 'analytics', hideForRoles: ['agent_senior', 'agent_junior', 'read_only', 'notary_sale', 'notary_rent', 'tenant_admin', 'office_admin'] },
      // office_admin entra con read_office: el backend lo acota a su oficina. read_only
      // también tiene read_office pero no debe ver facturación/metas → excluido por rol.
      // (01/09/2026) Antes iba `nativeOnly` porque "abría vacío o mentía" en otros tenants
      // (18/08) — pero eso era el leak de tenant, ya corregido. Validado sobre un tenant
      // Booster real (Elite): el reporte se scopea por tenant y muestra SUS datos, sin
      // filtrar los de Imperium. Las metas salen en cero hasta que el tenant cargue
      // objetivos (el "vs meta" queda neutro, no miente). Por eso ahora también en Booster.
      { title: 'Reporte Comercial', path: '/reporte-comercial', icon: <QueryStats fontSize="small" />, permission: ['analytics:read_all', 'analytics:read_office'], featureKey: 'reporte-comercial', hideForRoles: ['read_only'] },
      { title: 'Solicitudes BBVA', path: '/solicitudes-bbva', icon: <AccountBalance fontSize="small" />, featureKey: 'mortgage-bbva', hideForRoles: ['agent', 'agency_owner', 'agent_senior', 'agent_junior', 'read_only', 'notary_sale', 'notary_rent', 'booster_support'] },
      { title: 'Mapa de Cierre', path: '/mapa-cierre',  icon: <Map fontSize="small" />,         permission: 'properties:read',       featureKey: 'mapa-cierre', hideForRoles: ['tenant_admin', 'office_admin', 'booster_support'] },
      { title: 'Competencia',    path: '/competition',  icon: <EmojiEvents fontSize="small" />, permission: 'competition:read',      featureKey: 'competition', hideForRoles: ['tenant_admin', 'office_admin'], nativeOnly: true },
    ],
  },
  {
    label: 'Administración',
    items: [
      {
        title: 'Equipo',
        path: '/users',
        icon: <Group fontSize="small" />,
        permission: 'users:read',
        children: [
          { title: 'Agentes',             path: '/users',                 icon: <Group fontSize="small" />,         permission: 'users:read' },
          /* `nativeOnly`: el programa de referidos paga una comisión de la estructura de
             Imperium a quien trae un agente a Imperium. En otra inmobiliaria no hay a
             quién referir ni quién pague, así que ni el menú ni la pestaña del perfil van. */
          { title: 'Postulaciones',       path: '/postulaciones',         icon: <HowToReg fontSize="small" />,      permission: 'applications:read', featureKey: 'referral-program', nativeOnly: true },
          // `planFeature`: multi-oficina es de Black para arriba. Pro y Élite tienen UNA
          // oficina, así que el ABM no les sirve para nada. Imperium (nativo) no gatea por
          // plan — `hasPlanFeature` le da true. (18/08/2026, pedido del user)
          { title: 'Oficinas',            path: '/offices',               icon: <Business fontSize="small" />,      role: 'tenant_admin', planFeature: 'multi_office' },
          { title: 'Derivar propiedades', path: '/derivar/propiedades',   icon: <SwapHoriz fontSize="small" />,     permission: 'properties:transfer' },
        ],
      },
      // Solo office_admin: ve la participación de su oficina. El tenant_admin ya lo ve en Estadísticas.
      { title: 'Encuestas', path: '/admin/encuestas', icon: <PollOutlined fontSize="small" />, allowedRoles: ['office_admin'] },
      // Quiénes reportaron problemas o mejoras en su oficina — actividad, no contenido
      // (ver OfficeReportActivityPanel). Solo el admin de oficina: es su herramienta de
      // seguimiento del equipo. (pedido de dirección 31/07)
      { title: 'Reportes del equipo', path: '/admin/reportes-equipo', icon: <BugReport fontSize="small" />, allowedRoles: ['office_admin'] },
      {
        title: 'Facturación',
        path: '/admin/revenue',
        icon: <Payments fontSize="small" />,
        role: 'tenant_admin',
        // `nativeOnly`: comisiones, wallets, créditos y tarifas son la operativa
        // financiera DE IMPERIUM. La inmobiliaria booster factura su suscripción por
        // fuera del CRM. (18/08/2026, pedido del user)
        nativeOnly: true,
        children: [
          // Cada sub-item depende de su módulo. Si el flag correspondiente está
          // off, el sub-item desaparece — y si todos quedan ocultos, el padre
          // "Facturación" se oculta también (ver lógica de visibleChildren).
          // Orden pedido por dirección: Comisiones, Pagos, Wallets, Créditos, Tarifas.
          // Ganancias y Marketplace van al final: dependen del flag `marketplace`, que
          // en Imperium está off, así que no entran en ese orden.
          { title: 'Comisiones', path: '/admin/comisiones',  icon: <ReceiptLong fontSize="small" />,      role: 'tenant_admin', featureKey: 'finances' },
          { title: 'Pagos',      path: '/admin/payments',    icon: <Payments fontSize="small" />,         role: 'tenant_admin', featureKey: 'wallet' },
          { title: 'Wallets',    path: '/admin/wallets',     icon: <AccountBalanceWallet fontSize="small" />, role: 'tenant_admin', featureKey: 'wallet' },
          { title: 'Créditos',   path: '/admin/credits',     icon: <Diamond fontSize="small" />,          role: 'tenant_admin', featureKey: 'credits' },
          { title: 'Tarifas de operación', path: '/admin/operation-fees', icon: <Tune fontSize="small" />, role: 'tenant_admin', featureKey: 'wallet' },
          { title: 'Ganancias',  path: '/admin/revenue',     icon: <AttachMoney fontSize="small" />,      role: 'tenant_admin', featureKey: 'marketplace' },
          // Marketplace se mudó a Integraciones: acá lo tapaba el `nativeOnly` del grupo.
        ],
      },
      {
        title: 'Inversión',
        path: '/investments/catalogo',
        icon: <LocalOffer fontSize="small" />,
        permission: ['investments:catalog_manage', 'investments:catalog_manage_global'],
        hideIfPermission: 'menu:hide_inversion',
        // Toda la inversión publicitaria se paga/asigna con créditos.
        // Si el sistema de créditos está apagado, el grupo entero desaparece.
        featureKey: 'credits',
        // El admin de oficina NO ve el módulo de Inversión (solo tenant admin).
        hideForRoles: ['office_admin'],
        // `nativeOnly`: catálogo, paquetes y reinversión son el esquema de inversión
        // publicitaria DE IMPERIUM. (18/08/2026, pedido del user)
        nativeOnly: true,
        children: [
          { title: 'Catálogo',    path: '/investments/catalogo',    icon: <LocalOffer fontSize="small" />, permission: ['investments:catalog_manage', 'investments:catalog_manage_global'] },
          { title: 'Paquetes',    path: '/investments/paquetes',    icon: <Inventory2 fontSize="small" />, permission: ['investments:catalog_manage', 'investments:catalog_manage_global'] },
          { title: 'Reinversión', path: '/investments/reinversion', icon: <Tune fontSize="small" />,       permission: 'investments:reinvest_config' },
          { title: 'Reportes',    path: '/investments/reportes',    icon: <QueryStats fontSize="small" />, permission: 'investments:report' },
        ],
      },
      {
        title: 'Configuración',
        path: '/admin/web-content',
        icon: <Settings fontSize="small" />,
        // Config del TENANT (admin de tenant). La config GLOBAL de plataforma (home/flags) vive en el
        // grupo "Plataforma" del operador (platform_super/support), no acá.
        permission: 'web_content:manage',
        // `nativeOnly`: acá adentro está el editor de la web de Imperium y el feed de
        // Infocasas (que ya era nativeOnly). La inmobiliaria booster edita SU sitio en
        // "Mi Sitio Web", que sigue visible. (18/08/2026, pedido del user)
        nativeOnly: true,
        children: [
          { title: 'Editor web', path: '/admin/web-content', icon: <Language fontSize="small" />, permission: 'web_content:manage' },
          { title: 'Infocasas',  path: '/admin/infocasas',   icon: <RssFeed fontSize="small" />, role: 'tenant_admin', nativeOnly: true },
        ],
      },
      { title: 'Mi Sitio Web', path: '/mi-sitio', icon: <Language fontSize="small" />, permission: 'tenant_site:manage', hideIfPermission: 'menu:hide_mi_sitio' },
      // Conectar/desconectar MercadoLibre e Infocasas es configuración de TODO el
      // tenant, no de una oficina: un office_admin llega por `offices:settings` (que
      // tiene para su propia oficina) y desde acá tocaría las cuentas de la
      // inmobiliaria entera. La ruta también lo bloquea.
      { title: 'Integraciones', path: '/integrations', icon: <Hub fontSize="small" />, permission: 'offices:settings', hideForRoles: ['office_admin'] },
      // `nativeOnly`: el log de auditoría es la traza operativa interna de Imperium.
      // (18/08/2026, pedido del user)
      { title: 'Auditoría', path: '/audit-log', icon: <History fontSize="small" />, hideForRoles: ['agent', 'agent_senior', 'agent_junior', 'read_only', 'notary_sale', 'notary_rent', 'tenant_agent'], hideIfPermission: 'menu:hide_auditoria', nativeOnly: true },
      /**
       * ⛔ "Agente Gustavo" (Métricas / Auditoría / Ganancias) NO va en el menú.
       *
       * El 15/09/2026 se le sacó al admin de la inmobiliaria y quedó solo para soporte;
       * el 17/09 el dueño lo sacó también de ahí: "no hacemos nada con eso". Ocupaba un
       * grupo entero con tres hijos que nadie abría, y lo que sí se mira del cerebro de
       * Gustavo está en la consola de soporte (/support/gustavo).
       *
       * Las rutas `/gustavo-stats`, `/gustavo-audit` y `/gustavo-revenue` SIGUEN vivas en
       * `routes.crm.tsx`: quien las necesite entra por URL. Esto es sacar el acceso del
       * menú, no borrar las pantallas.
       */
    ],
  },
  {
    // Antes era "Gastos", con el cronograma solo. Al entrar el tablero el título dejó
    // de describir el grupo. (pedido 04/08)
    label: 'Otros',
    items: [
      // Tablero de tareas: en qué se está trabajando y en qué etapa va. Lo comparten
      // soporte y el admin del tenant — un solo tablero, que es el punto: si fueran
      // separados no se vería en qué anda cada uno.
      // `nativeOnly`: el tablero es el trabajo interno de Imperium. No alcanza con sacar
      // `booster_admin` de allowedRoles, porque el rol efectivo mapea booster_admin →
      // booster_support (ver `effRole`) y entraría igual por esa puerta. (12/08/2026)
      { title: 'Tablero',                path: '/tablero',                icon: <ViewKanban fontSize="small" />,   allowedRoles: ['booster_support', 'booster_admin', 'tenant_admin'], nativeOnly: true },
      // Mismo destino, para quien tiene el tablero PERSONAL habilitado por flag. Va como
      // ítem aparte porque el sidebar cruza `allowedRoles` y `featureKey` con AND, y acá
      // hacen falta en OR. `hideForRoles` evita que al equipo le aparezca duplicado si
      // alguna vez le tocan el flag.
      { title: 'Tablero',                path: '/tablero',                icon: <ViewKanban fontSize="small" />,   featureKey: 'tablero-personal', hideForRoles: ['booster_support', 'booster_admin', 'tenant_admin'] },
      // Cronograma de pagos/gastos de Imperium — exclusivo del rol 'booster_support'.
      // `nativeOnly` por lo mismo que el tablero: son los gastos DE IMPERIUM, y el rol
      // efectivo del operador de plataforma se mapea a booster_support.
      { title: 'Cronograma de pagos',    path: '/cronograma-pagos',       icon: <Payments fontSize="small" />,     allowedRoles: ['booster_support'], nativeOnly: true },
      /**
       * Consultas del formulario de contacto de la web.
       *
       * Hasta el 19/08/2026 no llegaban a ningún lado: creaban un cliente, el mensaje
       * quedaba en un campo editable de su ficha y nadie recibía aviso. 7 consultas desde
       * noviembre de 2025 sin una sola respuesta. Va en el menú y no escondida en una
       * pestaña justamente para que se vea sin buscarla. (pedido del user)
       *
       * `nativeOnly` (11/09/2026): es la bandeja de la web de IMPERIUM. A un tenant Booster
       * le abría vacía. Soporte la sigue viendo por la excepción de `nativeOnly`.
       */
      { title: 'Consultas web',          path: '/consultas-web',          icon: <ForumOutlined fontSize="small" />, allowedRoles: ['tenant_admin', 'booster_support', 'booster_admin'], nativeOnly: true },
    ],
  },
  {
    label: 'Soporte',
    items: [
      { title: 'Ayuda',                  path: '/help',         icon: <HelpOutline fontSize="small" />, hideForRoles: ['booster_support'] },
      // Home del rol 'booster_support' — KPIs del día, bugs urgentes, errores y salud Gustavo.
      // exact: true para que NO quede activo cuando estés en /support/analitica (peer).
      { title: 'Inicio',                 path: '/support',                icon: <HomeRounded fontSize="small" />,  allowedRoles: ['booster_support'], exact: true },
      // Inbox de bugs reportados + incidencias publicadas — exclusivo del rol 'booster_support'.
      { title: 'Bugs e incidencias',     path: '/admin/soporte',          icon: <WarningAmber fontSize="small" />, allowedRoles: ['booster_support'] },
      // Analítica de uso de plataforma — sub-items por dimensión.
      {
        title: 'Analítica',
        path: '/support/analitica',
        icon: <Insights fontSize="small" />,
        allowedRoles: ['booster_support'],
        children: [
          { title: 'Visión general', path: '/support/analitica',                 icon: <BarChart fontSize="small" />,    allowedRoles: ['booster_support'] },
          { title: 'Impacto del CRM', path: '/support/analitica?tab=impacto',  icon: <TrendingUp fontSize="small" />,  allowedRoles: ['booster_support'] },
          // Oculto temporalmente (pedido del user): la vista sigue existiendo (AiUsagePanel + tab=ai),
          // solo se saca del menú. Descomentar para volver a mostrarla.
          // { title: 'Gastos IA',      path: '/support/analitica?tab=ai',          icon: <Payments fontSize="small" />,     allowedRoles: ['booster_support'] },
          { title: 'Encuestas',      path: '/support/analitica?tab=surveys',     icon: <PollOutlined fontSize="small" />, allowedRoles: ['booster_support'] },
          { title: 'Agentes',        path: '/support/analitica?tab=agents',      icon: <Group fontSize="small" />,        allowedRoles: ['booster_support'] },
          { title: 'Clientes / Web', path: '/support/analitica?tab=clients',     icon: <PersonOutline fontSize="small" />, allowedRoles: ['booster_support'] },
          { title: 'Errores',        path: '/support/analitica?tab=errors',      icon: <ErrorOutline fontSize="small" />, allowedRoles: ['booster_support'] },
        ],
      },
      // Loop de bugs en vivo — qué está resolviendo el motor en tiempo real.
      // Oculto temporalmente (pedido del user): la vista sigue existiendo (ruta /support/loop),
      // solo se saca del menú. Descomentar para volver a mostrarla.
      // { title: 'Loop de bugs',           path: '/support/loop',           icon: <BugReport fontSize="small" />,    allowedRoles: ['booster_support'] },
      // Feature flags — para apagar features con bug rápidamente.
      // Oculto (pedido del user): duplicaba "Gestión de accesos", que ya apunta a /feature-flags.
      // La página sigue existiendo; descomentar para volver a mostrar este acceso de soporte.
      // { title: 'Feature Flags',          path: '/feature-flags',          icon: <Tune fontSize="small" />,         allowedRoles: ['booster_support'] },
    ],
  },
  {
    /**
     * Zona de Peligro.
     *
     * La consola de Gustavo es CROSS-TENANT —mezcla las charlas de todas las
     * inmobiliarias— y desde acá se puede apagar a Gustavo entero, prender y apagar tools,
     * tocar el prompt y tomar el control de la charla de cualquier agente. No es una
     * sección más de soporte, y por eso va aparte y con ese nombre.
     *
     * Se entra SOLO con el permiso `gustavo:console`, sin puerta de rol: ni
     * `booster_support` ni `booster_admin` alcanzan. Se le da a personas concretas.
     */
    label: 'Zona de Peligro',
    items: [
      {
        title: 'Gustavo',
        path: '/support/gustavo',
        icon: <AutoAwesome fontSize="small" />,
        permission: 'gustavo:console',
        children: [
          { title: 'En vivo',         path: '/support/gustavo?tab=live',         icon: <Hub fontSize="small" />,          permission: 'gustavo:console' },
          // Fallas: la vista que reemplaza a las cuatro ocultas. Llega el trabajo hecho
          // (qué falló + extracto + hipótesis), no señales crudas para ir a mirar.
          { title: 'Fallas',          path: '/support/gustavo?tab=incidents',    icon: <ErrorOutline fontSize="small" />, permission: 'gustavo:console' },
          // Mejora continua: pruebas, errores, correcciones y revalidación diaria.
          { title: 'Mejora continua', path: '/support/gustavo?tab=quality',      icon: <FactCheckOutlined fontSize="small" />, permission: 'gustavo:console' },
          /**
           * Tests: VUELVE AL MENÚ el 20/09/2026, a pedido del user.
           *
           * Se había ocultado el 31/07 junto con Inbox, Alucinaciones y Auditoría porque
           * no aportaban al día a día. Lo que cambió: la suite pasó de 13 a 103 casos y se
           * corre A MANO —el cron dejó de ejecutarla el 06/09 por el costo—, así que entrar
           * acá es ahora parte del trabajo, no una vista de consulta. Llegar por URL escrita
           * a mano no es una opción para algo que se usa antes de cada cambio de prompt.
           */
          { title: 'Tests',           path: '/support/gustavo?tab=golden',      icon: <Science fontSize="small" />,      permission: 'gustavo:console' },
          // Inbox / Alucinaciones / Auditoría: siguen OCULTOS (pedido del user 31/07).
          // Al restaurarlos hay que volver a importar el icono `Inbox`.
          // Su función se canaliza en "Fallas". Las vistas y sus rutas SIGUEN EXISTIENDO:
          // entrando a mano por /support/gustavo?tab=inbox | hallucinations | audit se ven
          // igual. Descomentar para restaurarlas.
          // { title: 'Inbox',           path: '/support/gustavo',                 icon: <Inbox fontSize="small" />,        permission: 'gustavo:console' },
          // { title: 'Alucinaciones',   path: '/support/gustavo?tab=hallucinations', icon: <WarningAmber fontSize="small" />, permission: 'gustavo:console' },
          // { title: 'Auditoría',       path: '/support/gustavo?tab=audit',       icon: <History fontSize="small" />,      permission: 'gustavo:console' },
          { title: 'Prompt + Reglas', path: '/support/gustavo?tab=prompt',      icon: <Code fontSize="small" />,         permission: 'gustavo:console' },
          { title: 'Core',            path: '/support/gustavo?tab=settings',    icon: <Settings fontSize="small" />,     permission: 'gustavo:console' },
          // Qué versión corre, con qué prompt y qué cambió en cada una.
          { title: 'Changelog',       path: '/support/gustavo?tab=changelog',   icon: <History fontSize="small" />,      permission: 'gustavo:console' },
        ],
      },
    ],
  },
  {
    /**
     * La documentación técnica del sistema — cómo funciona por dentro.
     *
     * Va al fondo y en su propia sección a propósito: no se abre todos los días como el
     * resto del menú de soporte, se abre cuando alguien tiene que entender algo. Meterla
     * entre las pantallas de trabajo diario la hacía competir con ellas.
     */
    label: 'Desarrollo',
    items: [
      { title: 'Documentación', path: '/support/documentacion', icon: <MenuBook fontSize="small" />, allowedRoles: ['booster_support'] },
      { title: 'Bitácora', path: '/support/bitacora', icon: <EventNoteRounded fontSize="small" />, allowedRoles: ['booster_support'] },
    ],
  },
];

interface SidebarProps {
  open:              boolean;
  onClose:           () => void;
  collapsed?:        boolean;
  onToggleCollapse?: () => void;
  topOffset?:        number;
}

export function Sidebar({ open, onClose, collapsed = false, onToggleCollapse, topOffset = 0 }: SidebarProps) {
  const theme   = useTheme();
  const { hasPermission, user } = usePermissions();
  // Contexto efectivo: si el staff de plataforma "entró" a un tenant (modo soporte),
  // el menú se arma con el plan/modo de ESE tenant, no con el del operador.
  const acting = useAppSelector((s) => s.auth.actingTenant);
  const { tenantMode: effectiveMode, hasPlanFeature } = useEffectiveTenant();
  // Identidad EFECTIVA: en modo soporte el menú se arma con el rol + permisos EMULADOS
  // del admin del tenant (así calza exacto con su login); si no, con los del usuario real.
  // root (booster_admin) ve el MISMO menú/vistas que soporte (booster_support) + la consola de plataforma.
  const effRole = acting ? (acting.role ?? 'tenant_admin') : (user?.role === 'booster_admin' ? 'booster_support' : user?.role);
  const effHasPermission = (p: string) => {
    if (!acting) return hasPermission(p);
    // Sesión de soporte previa al cambio (sin permisos guardados) → permisivo hasta re-entrar.
    return acting.permissions ? acting.permissions.includes(p) : true;
  };
  const { flags } = useFeatureFlags();
  const location  = useLocation();
  const navigate  = useNavigate();
  const dispatch  = useAppDispatch();
  const [logoutMutation] = useLogoutMutation();

  // Vista por rol de plataforma:
  //  - platform_super (operador) → SOLO la consola de plataforma (no el CRM de agente).
  //  - support → su grupo Soporte primero + la consola + el resto (filtrado por permisos).
  const orderedGroups = useMemo(() => {
    const platformGroup: NavGroup = {
      label: 'Plataforma',
      items: [
        /**
         * ⚠️ "Consola" (`/platform`) — OCULTA DEL MENÚ, la ruta SIGUE VIVA.
         *
         * Se sacó del sidebar el 12/08/2026 a pedido del dueño: hoy no aporta. Lo único
         * que tiene es el listado de tenants, y el botón "Entrar a" que traía se quitó
         * porque se confundía con la impersonación de `/users` —que es el camino real
         * para ver el CRM como lo ve un usuario de otra inmobiliaria—.
         *
         * NO se borró nada: la página, su endpoint y el listado siguen funcionando y se
         * llega escribiendo `/platform` en la URL. La idea es retomarla más adelante con
         * algo que valga la pena mostrar ahí; cuando eso pase, se descomenta esta línea.
         */
        /**
         * (12/09/2026) Vuelve como "Tenants": el listado ahora abre la FICHA de cada
         * inmobiliaria (`/platform/tenants/:id`) con métricas, administradores y la
         * suscripción —cuándo pagó, cuándo le toca, si hay tarjeta— que es lo que
         * soporte necesita sin ir a la base. El "Entrar a" sigue afuera.
         */
        // `exact`: `/platform` es prefijo de `/platform/coupons` y Tenants se prendía junto
        // con Cupones. La ficha (`/platform/tenants/:id`) lo prende por `activePrefix`.
        { title: 'Tenants', path: '/platform', icon: <Business fontSize="small" />, allowedRoles: ['booster_admin', 'booster_support'], exact: true, activePrefix: '/platform/tenants/' },
        /**
         * Cupones (12/09/2026, pedido del user): días gratis de un plan para un tenant, y
         * descuentos por porcentaje sobre un producto del marketplace. Los genera SOPORTE;
         * era una pantalla solo de root.
         */
        { title: 'Cupones', path: '/platform/coupons', icon: <LocalOffer fontSize="small" />, allowedRoles: ['booster_admin', 'booster_support'] },
        // Config GLOBAL de la plataforma (Fase 5: estos permisos son de platform_super; support tiene flags).
        { title: 'Configurar home',    path: '/home-layout',   icon: <DashboardCustomize fontSize="small" />, permission: 'home_layout:manage' },
        { title: 'Gestión de accesos', path: '/feature-flags', icon: <Tune fontSize="small" />,              permission: 'feature_flags:manage' },
      ],
    };
    // Modo soporte: el operador entró a un tenant → mostramos el CRM de ESE tenant
    // (no la consola de plataforma). El banner "Salir" lo devuelve a /platform.
    if (acting) return NAV_GROUPS;
    // Menú fijo de este usuario (menu:solo:*): reemplaza a todo el resto.
    const fijo = menuFijoDe(user?.effectivePermissions, NAV_GROUPS);
    if (fijo) return fijo;
    // root (booster_admin) y soporte (booster_support) comparten menú: grupo Soporte + consola de plataforma.
    if (effRole === 'booster_support') {
      const origSupport = NAV_GROUPS.find((g) => g.label === 'Soporte');
      const isRoot = user?.role === 'booster_admin';
      // root no necesita las secciones de CRM de tenant (Gestión/Análisis/Comunidad/Administración).
      const hidden = isRoot ? new Set(['Gestión', 'Análisis', 'Comunidad', 'Administración']) : new Set<string>();
      const rest = NAV_GROUPS.filter((g) => g !== origSupport && !hidden.has(g.label ?? ''));
      // Paths que root NO ve (la "Consola" queda embebida en el Inicio → /platform/inicio).
      const hiddenPaths = new Set(['/admin/soporte', '/support/analitica?tab=agents', '/platform']);
      const supportGroup = (isRoot && origSupport)
        ? {
            ...origSupport,
            items: origSupport.items
              .filter((it) => !hiddenPaths.has(it.path ?? ''))
              .map((it) => {
                // root: el "Inicio" es el Centro de Plataforma, no el wallboard de soporte.
                let out = it.path === '/support' ? { ...it, path: '/platform/inicio' } : it;
                if ('children' in out && out.children) out = { ...out, children: out.children.filter((c) => !hiddenPaths.has(c.path ?? '')) };
                return out;
              }),
          }
        : origSupport;
      // root: sacar "Consola" (/platform, va en el Inicio) y sumar "Tenants" (gestión + crear/cupones).
      const platformForRole = isRoot
        ? { ...platformGroup, items: [
            {
              title: 'Tenants', path: '/platform/tenants', icon: <Business fontSize="small" />,
              children: [
                { title: 'Inmobiliarias', path: '/platform/tenants', icon: <Business fontSize="small" /> },
                { title: 'Cupones',       path: '/platform/coupons', icon: <LocalOffer fontSize="small" /> },
              ],
            },
            ...platformGroup.items.filter((it) => !hiddenPaths.has(it.path ?? '')),
          ] }
        : platformGroup;
      return [supportGroup, platformForRole, ...rest].filter(Boolean) as NavGroup[];
    }
    return NAV_GROUPS;
  }, [effRole, user?.role, user?.effectivePermissions, acting]);

  /**
   * Novedades sin leer. El estado lo lleva el store compartido con el listado
   * (`novedadesLeidas`), no este componente: los dos tienen que ver lo mismo.
   *
   * Una novedad se marca leída cuando se ABRE (ruta /blog/:id), de a una. Antes bastaba
   * con pisar el listado para marcar las diez de golpe: el distintivo se borraba solo,
   * sin haber leído nada, y en la práctica nunca llegaba a verse. (pedido de dirección)
   */
  const { data: recentPosts = [] } = useGetRecentBlogPostsQuery();
  const seenIds = useNovedadesLeidas();
  useEffect(() => {
    const abierto = /^\/blog\/([^/?#]+)/.exec(location.pathname)?.[1];
    if (abierto) marcarNovedadLeida(abierto);
  }, [location.pathname]);
  const blogUnreadCount = recentPosts.filter((p) => !seenIds.has(p._id)).length;

  const { data: conversations = [] } = useListConversationsQuery(undefined, {
    // El contador de no leídos no puede quedar viejo. Volver a la pestaña o recuperar la
    // red revalidan: si el mensaje entró con el socket caído, Pusher no lo reenvía y sin
    // esto el badge miente hasta que la persona recarga a mano. (26/08/2026)
    refetchOnFocus: true, refetchOnReconnect: true,
  });
  const chatUnreadCount = conversations.reduce((acc, c) => acc + (c.unread ?? 0), 0);

  /**
   * Consultas del Agente Virtual sin atender. Del otro lado hay un cliente esperando y
   * el bot dejó de responderle, así que el número tiene que verse desde el menú: hoy
   * hay 29 esperando hace más de tres días porque nadie entraba a mirar.
   *
   * El endpoint ya devuelve solo lo que este usuario puede ver (sus propiedades, su
   * oficina o todo, según el rol), así que el contador es el suyo.
   *
   * El `skip` usa las MISMAS condiciones con las que se dibuja el ítem del menú —rol,
   * tenant nativo y flag—, no solo el flag. Pidiendo el contador con el flag a secas se
   * le reclamaba el número a quien no ve la sección: el notario y los agentes de los
   * tenants booster juntaron 1.062 errores 403 en una semana, uno cada dos minutos por
   * el polling, en páginas que no tienen nada que ver (el sidebar está en todas). El
   * backend rechazaba bien; el que preguntaba de más era el menú.
   */
  const puedeVerPhylax = usePuedeVerPhylaxCrm();
  const phylaxBooster = usePhylaxBooster();
  const { data: phylaxEsc } = useListCrmEscalationsQuery(
    { status: 'pending' },
    { skip: !puedeVerPhylax, pollingInterval: 120_000 },
  );
  const phylaxPendientes = phylaxEsc?.escalations?.length ?? 0;

  // Para office_admin, personalizamos el label "Propiedades de Imperium" con
  // el nombre de su oficina (ej. "Propiedades de Camino del Andaluz"). El
  // endpoint /properties ya filtra por oficina del user vía buildOfficeScope.
  const { data: myProfile } = useGetMyProfileQuery();
  const myOfficeName = (myProfile?.office as { name?: string } | undefined)?.name;

  function displayTitle(item: { title: string; path: string }): string {
    // office_admin: el sub-item "Imperium" sin query → es su oficina (backend
    // filtra). Le ponemos solo el nombre de la oficina (ej. "Shark") porque
    // el menú padre ya dice "Propiedades".
    if (
      user?.role === 'office_admin'
      && item.path === '/properties'
      && myOfficeName
    ) {
      return myOfficeName.replace(/^Imperium\s+/i, '');
    }
    return item.title;
  }

  // Los agentes aterrizan en "Mis propiedades" (no la vista corporativa de oficina).
  // Aplica al padre "Propiedades" cuando navega directo (modo colapsado); en expandido
  // solo despliega el submenú. office_admin/tenant_admin siguen en la corporativa.
  function resolveNavPath(path: string): string {
    const AGENT_ROLES = ['agent', 'tenant_agent', 'agent_senior', 'agent_junior'];
    return path === '/properties' && user?.role && AGENT_ROLES.includes(user.role)
      ? '/properties/mine'
      : path;
  }

  // Track which parent items have their submenu open
  // Auto-expand if current path is under that parent
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    NAV_GROUPS.forEach((g) =>
      g.items.forEach((item) => {
        if (item.children && location.pathname.startsWith(item.path)) {
          initial[item.path] = true;
        }
      }),
    );
    // Menú dinámico del root: "Tenants" (parent) agrupa /platform/tenants y /platform/coupons.
    if (location.pathname.startsWith('/platform/tenants') || location.pathname.startsWith('/platform/coupons')) {
      initial['/platform/tenants'] = true;
    }
    return initial;
  });

  const toggleSubmenu = (path: string) => {
    setOpenSubmenus((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const handleLogout = async () => {
    try { await logoutMutation().unwrap(); } catch { /* ignore */ }
    dispatch(logoutAction());
    dispatch(baseApi.util.resetApiState());
    // Persistir el estado limpio en localStorage antes de salir: si no, al volver
    // a Imperium PersistGate rehidrata la sesión vieja y la web pública te muestra logueado.
    try { await getPersistorRef()?.flush(); } catch { /* ignore */ }
    navigate('/login', { replace: true });
  };

  const isItemVisible = (item: NavItem | NavSubItem) => {
    // Los roles de portal (owner/bidder) no ven NINGÚN item del CRM — deben estar en sus propios portales
    if (user?.role === 'property_owner' || user?.role === 'auction_bidder') return false;

    // Agente en formación: allowlist, no allowlist-por-omisión. Varios items del menú
    // no declaran `permission` (Dashboard, Novedades, Parte diario…), así que gatearlo
    // por permisos lo dejaba entrar igual. Con lista blanca, un item nuevo tampoco se
    // le cuela por olvido. Ve Academy para formarse y Mensajes para hablar con su admin.
    if (effRole === 'agent_trainee') {
      return TRAINEE_PATHS.has(item.path ?? '');
    }

    // Examen de Tutoriales del CRM pendiente: sin Ayuda (ver features/examenPendiente).
    if (item.path === '/help' && user?.examenPendiente) return false;

    // Admin restringido: sus secciones ocultas no están (lib/restricciones.ts). Va con el
    // usuario REAL, no el efectivo: en modo soporte el menú es el del admin emulado.
    if (seccionOculta(user?.seccionesOcultas, item.path)) return false;

    // Grant puntual: quien tenga este permiso ve el ítem aunque su rol no esté en la
    // lista. Es para el dueño de la plataforma, que por rol es un `tenant_admin` más —
    // meter `tenant_admin` en `allowedRoles` le daría la consola a todas las inmobiliarias.
    if ('showIfPermission' in item && item.showIfPermission && effHasPermission(item.showIfPermission)) {
      return true;
    }

    // Gateo por rol/permiso usando la identidad EFECTIVA (en modo soporte = el admin
    // del tenant emulado; si no, el usuario logueado). Así el menú calza con el login.
    if (item.role && effRole !== item.role) return false;
    if ('allowedRoles' in item && item.allowedRoles && effRole && !item.allowedRoles.includes(effRole)) {
      return false;
    }
    if (item.permission) {
      const perms = Array.isArray(item.permission) ? item.permission : [item.permission];
      if (!perms.some((p) => effHasPermission(p))) return false;
    }
    // hideForRoles: si el rol efectivo está en la lista, ocultar
    if ('hideForRoles' in item && item.hideForRoles && effRole && item.hideForRoles.includes(effRole)) {
      return false;
    }
    // Ocultar POR USUARIO. Ver el comentario del tipo: es un permiso al revés, así que por
    // defecto no lo tiene nadie y ningún menú cambia hasta que se lo asignan a alguien.
    // `effHasPermission` y no `hasPermission`: en modo soporte el menú se arma con los
    // permisos EMULADOS del admin del tenant, y este chequeo tiene que mirar los mismos —
    // si no, soporte vería un menú distinto al que ve el admin al que está emulando.
    if ('hideIfPermission' in item && item.hideIfPermission && effHasPermission(item.hideIfPermission)) {
      return false;
    }
    /**
     * nativeOnly: features Imperium-only. Se muestran SOLO con el modo nativo confirmado.
     *
     * Antes ocultaba únicamente cuando el modo era exactamente 'booster', así que
     * cualquiera SIN tenant resuelto las veía igual: las cuentas de plataforma
     * (`booster_admin` / `booster_support`) no tienen tenantId ni oficina, `tenantMode`
     * les llega undefined y pasaban de largo. Por eso desde el QA de Booster se veía el
     * menú de Phylax, que es exclusivo de Imperium.
     *
     * Ahora la condición es afirmativa: si el modo no es 'native', no se muestra. El
     * riesgo de dejar a alguien sin menú es acotado — de todos los usuarios activos,
     * solo 5 no resuelven el modo, y son las 4 cuentas de plataforma más un owner de
     * prueba; todo agente o admin real lo trae. (12/08/2026)
     *
     * La única excepción es el SOPORTE de Imperium: no tiene tenant propio, pero su
     * trabajo es justamente Imperium —de ahí revisa las charlas del Agente Virtual— y
     * no puede entrar en modo soporte a mirarlo desde adentro, porque `booster_support`
     * no tiene `tenants:enter`. Sin esta excepción se quedaba sin la sección y sin forma
     * de llegar a ella. `booster_admin` (el operador de la plataforma Booster) NO entra
     * acá: es el que veía el menú de Phylax desde el QA de Booster.
     */
    // Contra el rol REAL, no contra `effRole` — que mapea booster_admin →
    // booster_support para emular la vista. Con el efectivo, el operador de plataforma
    // entraba por la excepción y seguía viendo todo.
    const esSoporteDeImperium = !effectiveMode && !acting && user?.role === 'booster_support';
    if ('nativeOnly' in item && item.nativeOnly && effectiveMode !== 'native' && !esSoporteDeImperium) {
      return false;
    }
    if ('soloConPhylax' in item && item.soloConPhylax && !puedeVerPhylax && !phylaxBooster.esBooster) return false;
    /**
     * boosterOnly: el inverso. Features que son SOLO para las inmobiliarias suscriptoras y
     * que en Imperium no van.
     *
     * ⛔ LA CONDICIÓN ES AFIRMATIVA —`!== 'booster'`, no `=== 'native'`— por el mismo motivo
     * que está explicado arriba para `nativeOnly`: las cuentas de plataforma
     * (`booster_admin` / `booster_support`) no tienen tenant, les llega `undefined`, y con la
     * condición al revés pasarían de largo y verían el menú. Si el modo no está confirmado
     * como 'booster', no se muestra.
     *
     * ⛔ Y NO LLEVA la excepción del soporte de Imperium que sí tiene `nativeOnly`: ahí la
     * excepción existe porque el trabajo de ese rol ES Imperium. Acá sería justo al revés.
     */
    if ('boosterOnly' in item && item.boosterOnly && effectiveMode !== 'booster') {
      return false;
    }
    // feature flag: si el flag está explícitamente en false, ocultar.
    // Si todavía no llegó la respuesta de flags (loading) asumimos visible.
    if ('featureKey' in item && item.featureKey) {
      if (Object.keys(flags).length > 0 && flags[item.featureKey] === false) return false;
    }
    // feature del PLAN: si el plan del tenant no la incluye, ocultar (ej. "Corporativo" → multi_office).
    if ('planFeature' in item && item.planFeature && !hasPlanFeature(item.planFeature)) {
      return false;
    }
    // Si es un item padre con children y TODOS los children están ocultos
    // (por flag/role/permission), ocultar el padre también — evita dejar
    // grupos vacíos colgando en el sidebar.
    if ('children' in item && item.children && item.children.length > 0) {
      const anyChildVisible = item.children.some((child) => isItemVisible(child));
      if (!anyChildVisible) return false;
    }
    return true;
  };

  // Paleta de la marca activa del CRM (Booster emerald), no la dorada fija.
  const P = getCrmPalette();
  const golden = P.brand.golden;
  const goldenSubtle = P.brand.goldenSubtle;
  // Item seleccionado: en modo CLARO un dorado MÁS OSCURO (goldenDark) para que resalte
  // contra el sidebar claro; en oscuro, el dorado base.
  const { mode } = useThemeMode();
  const activeGold = mode === 'light' ? P.brand.goldenDark : golden;

  const itemSx = (active: boolean, highlighted = false): SxProps<Theme> => {
    if (highlighted) {
      return {
        borderRadius: 1.5,
        py: 0.85,
        px: 1.5,
        position: 'relative',
        color: P.brand.onAccent,
        background: active
          ? `linear-gradient(135deg, ${P.brand.goldenLight} 0%, ${golden} 100%)`
          : `linear-gradient(135deg, ${golden} 0%, ${P.brand.goldenMid} 100%)`,
        boxShadow: `0 4px 14px ${alpha(golden, 0.35)}`,
        fontWeight: 700,
        '&:hover': {
          background: `linear-gradient(135deg, ${P.brand.goldenLight} 0%, ${golden} 100%)`,
          boxShadow: `0 6px 20px ${alpha(golden, 0.5)}`,
          transform: 'translateY(-1px)',
        },
        '& .MuiListItemIcon-root': { color: `${P.brand.onAccent} !important`, opacity: '1 !important' },
        '& .MuiListItemText-primary': { color: `${P.brand.onAccent} !important`, fontWeight: '800 !important' },
        transition: 'all 0.2s ease',
      };
    }
    return {
      borderRadius: 1.5,
      py: 0.85,
      px: 1.5,
      position: 'relative',
      color: active ? activeGold : 'text.secondary',
      bgcolor: active ? goldenSubtle : 'transparent',
      '&::before': active ? {
        content: '""',
        position: 'absolute',
        left: 0, top: '20%', bottom: '20%',
        width: 3, borderRadius: '0 3px 3px 0',
        bgcolor: activeGold,
      } : {},
      '&:hover': {
        bgcolor: active ? goldenSubtle : alpha(golden, 0.06),
        color: active ? activeGold : 'text.primary',
      },
      transition: 'all 0.15s ease',
    };
  };

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' }}>

      {/* Brand row — en desktop el logo y la hamburguesa viven en el topbar,
          así que acá solo agregamos un padding superior. En mobile (drawer
          temporal, sin onToggleCollapse) mostramos el logo como antes. */}
      {onToggleCollapse ? (
        <Box sx={{ pt: 3 }} />
      ) : (
        <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
          <BoosterMark height={32} showWordmark={false} logoUrl={user?.tenant?.branding?.logoUrl} name={user?.tenant?.branding?.displayName} />
        </Box>
      )}

      {/* User card */}
      {user && (
        <Box sx={{ px: collapsed ? 1 : 2, pb: 2 }}>
          <Box
            component={NavLink}
            to={user?.role === 'booster_admin' ? '/platform/inicio' : user?.role === 'booster_support' ? '/support' : '/profile'}
            onClick={onClose}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              p: 1.25,
              borderRadius: 2,
              border: `1px solid ${alpha(golden, 0.18)}`,
              background: `linear-gradient(135deg, ${alpha(golden, 0.07)} 0%, transparent 100%)`,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              textDecoration: 'none',
              transition: 'all 0.15s ease',
              '&:hover': {
                border: `1px solid ${alpha(golden, 0.4)}`,
                background: `linear-gradient(135deg, ${alpha(golden, 0.13)} 0%, transparent 100%)`,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0, top: 0, bottom: 0,
                width: 3,
                background: P.gradients.golden,
              },
            }}
          >
            <AgentAvatar
              src={user.avatar || undefined}
              initials={!user.avatar ? `${user.name[0]}${user.lastName[0]}` : undefined}
              size={36}
              tier={(myProfile as { topGrantedCircle?: CircleTier } | undefined)?.topGrantedCircle}
              sx={{
                background: P.gradients.golden,
                color: P.brand.onAccent,
                boxShadow: `0 2px 8px ${alpha(golden, 0.3)}`,
                border: 'none',
              }}
            />
            {!collapsed && (
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography fontWeight={700} noWrap fontSize={13} color="text.primary" sx={{ lineHeight: 1.3 }}>
                  {user.name} {user.lastName}
                </Typography>
                {/* ⛔ SIN EL ROL. Estaba acá ("tenant admin", "Agente"…) y ocupaba el
                    renglón que ahora dice de qué plataforma es la pantalla, que es el dato
                    que hacía falta: el rango uno ya lo sabe —es el suyo— y la plataforma no.
                    Va en su propia línea porque pegado al nombre lo dejaba en "Gu…". */}
                <SelloDePlataforma rolReal={user.role} compacto />
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Divider sx={{ mx: 2, opacity: 0.5 }} />

      {/* Nav */}
      <Box sx={{
        flex: 1, overflowY: 'auto', py: 1.5, px: collapsed ? 0.75 : 1.5,
        // Scrollbar visible y agarrable (antes 3px casi invisible — un agente
        // pidió "una barrita para deslizar mejor" el menú).
        '&::-webkit-scrollbar': { width: 8 },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: alpha(P.brand.golden, 0.35), borderRadius: 4,
          '&:hover': { bgcolor: alpha(P.brand.golden, 0.6) },
        },
        scrollbarWidth: 'thin',
        scrollbarColor: `${alpha(P.brand.golden, 0.4)} transparent`,
      }}>
        {orderedGroups.map((group, gi) => {
          const visibleItems = group.items.filter(isItemVisible)
            // Menú fijo: de dos ítems con el mismo path, el primero visible.
            .filter((it, i, arr) => !group.fijo || arr.findIndex((x) => x.path === it.path) === i);
          if (visibleItems.length === 0) return null;
          return (
            <Box key={gi} sx={{ mb: 0.5 }}>
              {group.label && !collapsed && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block', px: 1.5, pt: gi > 0 ? 1.5 : 0.5, pb: 0.5,
                    color: 'text.disabled', fontWeight: 700,
                    fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
                  }}
                >
                  {group.label}
                </Typography>
              )}
              {/* Separador. Los grupos SUELTOS (sin encabezado, ej. Phylax) lo necesitan
                  también en expandido: sin él quedan pegados al grupo de arriba y se leen
                  como un ítem más de esa sección —Phylax parecía parte de "Análisis"—.
                  Con encabezado alcanza el título, salvo colapsado, donde no se ve.
                  (pedido de dirección 31/07) */}
              {gi > 0 && (collapsed || !group.label) && (
                <Divider sx={{ my: 1, opacity: 0.3 }} />
              )}
              <List disablePadding>
                {visibleItems.map((item) => {
                  const hasChildren = !!item.children?.length;
                  const isSubmenuOpen = openSubmenus[item.path] ?? false;
                  // Parent is "active" if we're exactly on its path (not on a child path)
                  // "Activo" = match EXACTO si tiene children o exact=true; sino,
                  // match exact + sub-rutas reales (item.path + '/') para evitar
                  // que items peer que comparten prefijo se prendan juntos.
                  const parentActive = estaActivo(item, location.pathname);

                  const collapsedItemSx = {
                    ...itemSx(parentActive),
                    justifyContent: 'center',
                    px: 0,
                    minHeight: 44,
                  };

                  return (
                    <Box key={item.path}>
                      <ListItem disablePadding sx={{ mb: 0.25 }}>
                        {hasChildren ? (
                          // En modo colapsado, el padre navega directo al path; en modo expandido, toggle submenu
                          collapsed ? (
                            <Tooltip title={item.title} placement="right">
                              <ListItemButton
                                component={NavLink}
                                to={resolveNavPath(item.path)}
                                onClick={onClose}
                                sx={collapsedItemSx}
                              >
                                <ListItemIcon sx={{ minWidth: 0, color: 'inherit', justifyContent: 'center', opacity: parentActive ? 1 : 0.75 }}>
                                  {item.icon}
                                </ListItemIcon>
                              </ListItemButton>
                            </Tooltip>
                          ) : (
                            <ListItemButton
                              onClick={() => toggleSubmenu(item.path)}
                              sx={itemSx(false)}
                            >
                              {item.titleImage ? (
                                // Logo-imagen de marca en lugar de icono+texto (ej. Phylax).
                                // Misma escala visual que las filas de texto del menú. En modo
                                // claro usa la variante "-light" (wordmark en color, no blanco).
                                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: '1 1 auto' }}>
                                  <Box component="img"
                                    src={mode === 'light' ? item.titleImage.replace(/\.svg$/, '-light.svg') : item.titleImage}
                                    alt={item.title}
                                    sx={{ height: 20, width: 'auto', display: 'block', opacity: parentActive ? 1 : 0.9 }} />
                                </Box>
                              ) : (
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', minWidth: 0, flex: '1 1 auto', gap: 2.25 }}>
                                <Box sx={{ color: 'inherit', opacity: 0.65, flexShrink: 0, '& svg': { display: 'block' } }}>
                                  {item.icon}
                                </Box>
                                <Typography
                                  component="span"
                                  sx={{ fontSize: 13.5, fontWeight: 500, letterSpacing: 0.1, color: 'inherit', flex: 1, my: 0 }}
                                >
                                  {item.title}
                                </Typography>
                              </Box>
                              )}
                              {isSubmenuOpen
                                ? <ExpandMore sx={{ fontSize: 16, color: 'inherit', opacity: 0.7, flexShrink: 0 }} />
                                : <ChevronRight sx={{ fontSize: 16, color: 'inherit', opacity: 0.5, flexShrink: 0 }} />}
                            </ListItemButton>
                          )
                        ) : (
                          <Tooltip title={collapsed ? item.title : ''} placement="right" disableHoverListener={!collapsed}>
                            <ListItemButton
                              component={NavLink}
                              to={item.path}
                              onClick={onClose}
                              sx={collapsed ? collapsedItemSx : itemSx(parentActive, item.highlighted)}
                            >
                              {/* Marca propia (ej. Phylax): el logo REEMPLAZA icono+texto, igual
                                  que en la rama con submenú. Colapsado sigue usando `icon`. */}
                              {item.titleImage && !collapsed ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: '1 1 auto' }}>
                                  <Box component="img"
                                    src={mode === 'light' ? item.titleImage.replace(/\.svg$/, '-light.svg') : item.titleImage}
                                    alt={item.title}
                                    sx={{ height: 20, width: 'auto', display: 'block', opacity: parentActive ? 1 : 0.9 }} />
                                </Box>
                              ) : (
                              <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, color: 'inherit', opacity: parentActive ? 1 : 0.65, mr: 0, '& svg': { display: 'block', mx: 0 }, justifyContent: 'flex-start !important' }}>
                                {/* Expandido manda el chip dorado y el ícono va limpio. Colapsado
                                    no tiene dónde poner el chip, así que ahí sí va el contador
                                    —en dorado, nunca en rojo: el rojo es alarma y esto es una
                                    novedad. (pedido de dirección 31/07) */}
                                {item.path === '/blog' && blogUnreadCount > 0 && collapsed ? (
                                  <Badge
                                    badgeContent={blogUnreadCount}
                                    sx={{ '& .MuiBadge-badge': { fontSize: 9, minWidth: 15, height: 15, px: 0.4, bgcolor: golden, color: P.brand.onAccent, fontWeight: 700 } }}
                                  >
                                    {item.icon}
                                  </Badge>
                                ) : item.path === '/chat' && chatUnreadCount > 0 ? (
                                  <Badge badgeContent={chatUnreadCount} color="warning" sx={{ '& .MuiBadge-badge': { fontSize: 9, minWidth: 15, height: 15, px: 0.4 } }}>
                                    {item.icon}
                                  </Badge>
                                ) : item.path === '/agente' && phylaxPendientes > 0 && collapsed ? (
                                  <Badge badgeContent={phylaxPendientes} color="warning" sx={{ '& .MuiBadge-badge': { fontSize: 9, minWidth: 15, height: 15, px: 0.4 } }}>
                                    {item.icon}
                                  </Badge>
                                ) : item.icon}
                              </ListItemIcon>
                              )}
                              {!collapsed && !item.titleImage && (
                                <>
                                  <ListItemText
                                    primary={item.title}
                                    primaryTypographyProps={{ fontSize: 13.5, fontWeight: (parentActive || (item.path === '/blog' && blogUnreadCount > 0)) ? 700 : 500, letterSpacing: 0.1 }}
                                    sx={{ my: 0, pl: 0 }}
                                  />
                                  {/* Novedades sin leer: el contador sobre el ícono es chico y se
                                      pasa por alto. El chip dice qué pasó con palabras. */}
                                  {/* Consultas sin atender: el chip dice cuántas, que es
                                      lo que hace entrar. */}
                                  {item.path === '/agente' && phylaxPendientes > 0 && (
                                    <Chip
                                      label={phylaxPendientes}
                                      size="small"
                                      color="warning"
                                      sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                                    />
                                  )}
                                  {item.path === '/blog' && blogUnreadCount > 0 && (
                                    <Chip
                                      label={blogUnreadCount > 1 ? `${blogUnreadCount} nuevas` : 'Nueva'}
                                      size="small"
                                      sx={{
                                        height: 18, fontSize: 10, fontWeight: 700,
                                        bgcolor: golden, color: P.brand.onAccent,
                                      }}
                                    />
                                  )}
                                  {item.badge && (
                                    <Chip
                                      label={item.badge}
                                      size="small"
                                      sx={{
                                        height: 18, fontSize: 10, fontWeight: 700,
                                        bgcolor: item.highlighted ? golden : alpha(golden, 0.18),
                                        color:   item.highlighted ? P.brand.onAccent : golden,
                                        border:  item.highlighted ? `1px solid ${alpha(golden, 0.35)}` : 'none',
                                      }}
                                    />
                                  )}
                                </>
                              )}
                            </ListItemButton>
                          </Tooltip>
                        )}
                      </ListItem>

                      {/* Submenu children */}
                      {hasChildren && !collapsed && (
                        <Collapse in={isSubmenuOpen} timeout="auto" unmountOnExit>
                          <List
                            disablePadding
                            sx={{
                              pb: 0.5,
                              pl: 3.5,
                              position: 'relative',
                              // Línea vertical dorada sutil tipo tree-branch
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 20,
                                top: 0,
                                bottom: 6,
                                width: '2px',
                                borderRadius: 1,
                                bgcolor: alpha(golden, 0.18),
                              },
                            }}
                          >
                            {item.children!.filter(isItemVisible).map((child) => {
                              const childActive = isChildPathActive(child.path, location.pathname, location.search);
                              return (
                                <ListItem key={child.path} disablePadding sx={{ mb: 0.25 }}>
                                  <ListItemButton
                                    component={NavLink}
                                    to={child.path}
                                    onClick={onClose}
                                    sx={{
                                      borderRadius: 1.5,
                                      py: 0.65,
                                      px: 1.25,
                                      position: 'relative',
                                      color: childActive ? activeGold : 'text.secondary',
                                      bgcolor: childActive ? goldenSubtle : 'transparent',
                                      '&::before': childActive ? {
                                        content: '""',
                                        position: 'absolute',
                                        left: 0, top: '25%', bottom: '25%',
                                        width: 2, borderRadius: '0 2px 2px 0',
                                        bgcolor: alpha(activeGold, 0.6),
                                      } : {},
                                      '&:hover': {
                                        bgcolor: childActive ? goldenSubtle : alpha(golden, 0.06),
                                        color: childActive ? activeGold : 'text.primary',
                                      },
                                      transition: 'all 0.15s ease',
                                    }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 32, color: 'inherit', opacity: childActive ? 1 : 0.55 }}>
                                      {child.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={displayTitle(child)}
                                      primaryTypographyProps={{ fontSize: 12.5, fontWeight: childActive ? 700 : 500, letterSpacing: 0.1 }}
                                      sx={{ my: 0, pl: 0 }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              );
                            })}
                          </List>
                        </Collapse>
                      )}
                    </Box>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>

      {/* Footer: solo logout */}
      <Box sx={{ px: collapsed ? 0.75 : 1.5, pb: 1.5 }}>
        <Divider sx={{ mb: 1.25, opacity: 0.4 }} />

        <Tooltip title={collapsed ? 'Cerrar sesión' : ''} placement="right" disableHoverListener={!collapsed}>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 1.5,
              px: collapsed ? 0 : 1.5, py: 0.85,
              justifyContent: collapsed ? 'center' : 'flex-start',
              color: 'text.secondary',
              '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08), color: 'error.main' },
              transition: 'all 0.15s ease',
            }}
          >
            <ListItemIcon sx={{ minWidth: collapsed ? 0 : 32, color: 'inherit', justifyContent: 'center' }}>
              <Logout fontSize="small" />
            </ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary="Cerrar sesión"
                primaryTypographyProps={{ fontSize: 13.5, fontWeight: 500 }}
              />
            )}
          </ListItemButton>
        </Tooltip>

        {/* Colapsado no entra en la ficha del usuario (solo se ve el avatar): ahí sí va
            al pie, junto a la versión, como una bandera sola. */}
        {collapsed && <SelloDePlataforma collapsed rolReal={user?.role} />}
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 0.5, fontSize: 10, letterSpacing: '0.06em', opacity: 0.5 }}>
          v3.0
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          // El menú mobile debe quedar POR ENCIMA del topbar (AppBar = drawer + 1),
          // si no se abre por detrás de la barra de navegación.
          zIndex: (theme) => theme.zIndex.drawer + 2,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', border: 'none', boxShadow: 6 },
        }}
      >
        {content}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
            top: `${topOffset}px`,
            height: `calc(100% - ${topOffset}px)`,
            boxSizing: 'border-box',
            borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
            overflowX: 'hidden',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        {content}
      </Drawer>
    </>
  );
}
